import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  ConfirmationDialogCloseResult,
  ConfirmationDialogData,
} from '../../shared/components/confirmation-dialog/confirmation-dialog.types';
import {
  ManualReminderConfirmationData,
  ManualReminderConfirmationDialogComponent
} from '../../shared/components/confirmation-dialog/manual-reminder-confirmation-dialog.component';
import {
  ManualReminderDeliveryFailureRow,
  ManualReminderDeliveryResultDialogComponent
} from '../../shared/components/confirmation-dialog/manual-reminder-delivery-result-dialog.component';
import { ConfirmationCreationDraftInvoiceComponent, ConfirmationCreationDraftInvoiceData, InvoiceCreationResult } from '../../shared/components/billing/confirmation-creation-draft-invoice/confirmation-creation-draft-invoice.component';
import { ConfirmationFinalizingInvoiceComponent, ConfirmationFinalizingInvoiceData, InvoiceFinalizationResult } from '../../shared/components/billing/confirmation-finalizing-invoice/confirmation-finalizing-invoice.component';
import { ChooseDialogComponent, ChooseDialogData, ChooseDialogResult } from '../../shared/components/confirmation-dialog/choose-dialog.component';
import { ChooseThreeDialogComponent, ChooseThreeDialogData, ChooseThreeDialogResult } from '../../shared/components/confirmation-dialog/choose-three-dialog.component';
import { DropFileDialogComponent, DropFileDialogData, DropFileDialogResult } from '../../shared/components/confirmation-dialog/drop-file-dialog.component';
import { InputTextDialogComponent, InputTextDialogData, InputTextDialogResult } from '../../shared/components/confirmation-dialog/input-text-dialog.component';
import { ValidateTimesheetDialogComponent, ValidateTimesheetDialogData, ValidateTimesheetDialogResult } from '../../shared/components/confirmation-dialog/validate-timesheet-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { ErrorNotificationDialogComponent } from '../components/error-notification-dialog/error-notification-dialog.component';
import {
  ErrorNotificationContent,
  ErrorNotificationLink,
  errorNotificationDialogWidth,
  resolveErrorNotificationContent,
} from './error-notification-rules';
import { DialogTone } from './dialog-shell-rules';
import { escapeHtml } from '../utils/html.util';
import { ActivityService } from './activity.service';
import { DealsService } from './deals.service';
import { ConfigApiService } from './config-api.service';

/** Options of {@link PopupAndNotifService.confirm}. */
export interface ConfirmOptions {
  confirmText?: string;
  cancelText?: string;
  /** `danger` for a destructive action: pink confirm button and warning icon. */
  tone?: DialogTone;
  /** Tabler icon in the header; defaults to the tone's icon. */
  icon?: string | null;
  width?: string;
}

/**
 * The single entry point for every popup of the app: confirmations, choices, text input, file
 * drop, error dialogs, toasts and the global spinner. Pages never open a generic dialog
 * themselves; a dedicated dialog (business data, form) is built on core/ui/dialog-shell so it
 * looks the same. Doc: docs/ArturPro/UI_DESIGN_SYSTEM.md.
 */
@Injectable({
  providedIn: 'root'
})
export class PopupAndNotifService {

  constructor(
    private dialog: MatDialog,
    private toast: ToastrService,
    private activityservice: ActivityService,
    private dealservice: DealsService,
    private configApiService: ConfigApiService
  ) {}

    // Global spinner with an optional message
    private spinnerLoadingSubject = new BehaviorSubject<boolean>(false);
    private spinnerMessageSubject = new BehaviorSubject<string>('');
    public spinnerLoading$ = this.spinnerLoadingSubject.asObservable();
    public spinnerMessage$ = this.spinnerMessageSubject.asObservable();

  /**
   * Asks for a confirmation and resolves true only when the user confirmed (Cancel, Escape or a
   * click outside resolve false). The message is bound as sanitized HTML: escape values that come
   * from the database or from user input (escapeHtml in core/utils/html.util.ts).
   */
  confirm(title: string, message: string, options: ConfirmOptions = {}): Promise<boolean> {
    return firstValueFrom(
      this.openConfirmation(
        {
          title,
          message,
          confirmText: options.confirmText,
          cancelText: options.cancelText,
          tone: options.tone,
          icon: options.icon,
        },
        '300ms',
        '200ms',
        true,
        options.width ?? '440px'
      ).pipe(map((result) => this.normalizeConfirmationAccepted(result)))
    );
  }

  /**
   * Opens the shared confirmation dialog.
   * @param data dialog content and options
   * @param enterAnimationDuration enter animation duration (optional)
   * @param exitAnimationDuration exit animation duration (optional)
   * @returns the close result: true when confirmed, false when cancelled, an object for the
   *   checkbox / binary-choice variants, null when closed from the header
   */
  openConfirmation(
    data: ConfirmationDialogData,
    enterAnimationDuration: string = '300ms',
    exitAnimationDuration: string = '200ms',
    disableClose: boolean = true,
    width: string = '440px'
  ): Observable<ConfirmationDialogCloseResult> {
    const dialogRef = this.dialog.open<
      ConfirmationDialogComponent,
      ConfirmationDialogData,
      ConfirmationDialogCloseResult
    >(ConfirmationDialogComponent, {
      width,
      maxWidth: '92vw',
      data: data,
      enterAnimationDuration,
      exitAnimationDuration,
      disableClose
    });

    return dialogRef.afterClosed() as Observable<ConfirmationDialogCloseResult>;
  }

  private normalizeConfirmationAccepted(result: ConfirmationDialogCloseResult): boolean {
    return result === true || (typeof result === 'object' && !!result?.confirmed);
  }

  private normalizeConfirmationResult(result: ConfirmationDialogCloseResult): boolean | null {
    if (result === null) {
      return null;
    }
    return this.normalizeConfirmationAccepted(result);
  }

  /**
   * Destructive confirmation: the title names what is deleted, the confirm button is pink.
   * @param itemName name of the item to delete (escaped here: it is bound as HTML)
   * @param itemType kind of item, e.g. 'fee' (optional)
   */
  confirmDelete(itemName: string, itemType: string = 'item'): Observable<boolean> {
    const name = escapeHtml(itemName);
    return this.openConfirmation({
      title: `Delete the ${itemType} "${itemName}"?`,
      message: `The ${escapeHtml(itemType)} <strong>${name}</strong> will be deleted.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      tone: 'danger',
    }).pipe(map((result) => this.normalizeConfirmationAccepted(result)));
  }

  /**
   * Confirmation with configurable buttons.
   * @param title dialog title: say what is about to happen, never just "Are you sure?"
   * @param message confirmation message (sanitized HTML)
   * @param confirmText confirm button label: repeat the verb ("Send reminder", not "Yes")
   * @param cancelText cancel button label
   * @returns true when confirmed, false when cancelled, null when closed from the header
   */
  confirmAction(
    title: string, 
    message: string, 
    confirmText: string = 'Confirm',
    cancelText: string = 'Cancel',
    options?: {
      disableClose?: boolean;
      dismissText?: string;
      width?: string;
      reverseButtons?: boolean;
      tone?: DialogTone;
    }
  ): Observable<boolean | null> {
    return this.openConfirmation(
      {
        title: title,
        message: message,
        confirmText: confirmText,
        cancelText: cancelText,
        confirmColor: 'primary',
        tone: options?.tone,
        dismissText: options?.dismissText,
        reverseButtons: options?.reverseButtons ?? false
      },
      '300ms',
      '200ms',
      options?.disableClose ?? true,
      options?.width ?? '440px'
    ).pipe(map((result) => this.normalizeConfirmationResult(result)));
  }

  confirmManualReminder(
    data: ManualReminderConfirmationData,
    options?: { disableClose?: boolean }
  ): Observable<boolean | null> {
    const dialogRef = this.dialog.open(ManualReminderConfirmationDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      data,
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '200ms',
      disableClose: options?.disableClose ?? true
    });

    return dialogRef.afterClosed();
  }

  showManualReminderDeliveryResult(
    data: {
      reminderLevel: number;
      deliverySummary: 'all_failed' | 'partial_success';
      failures: ManualReminderDeliveryFailureRow[];
    },
    options?: { disableClose?: boolean }
  ): Observable<boolean | null> {
    const dialogRef = this.dialog.open(ManualReminderDeliveryResultDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      data,
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '200ms',
      disableClose: options?.disableClose ?? false
    });

    return dialogRef.afterClosed();
  }

  /**
   * Confirmation before creating a draft invoice.
   * @param feesCount number of selected fees
   * @param corporate company name
   * @param address company address
   * @param city city
   * @param country country
   * @returns Observable<InvoiceCreationResult>
   */
  confirmInvoiceCreation(
    feesCount: number,
    corporate: string,
    address: string,
    city: string,
    country: string,
    pennylaneCountry?: string | null,
    sdi?: string | null,
    feeVatSummary?: Array<{ name: string; billing_code: string; vat_rate: number | null }>
  ): Observable<InvoiceCreationResult> {
    const message = `You are about to create an invoice with ${feesCount} fee(s) selected.`;
    
    const dialogRef = this.dialog.open(ConfirmationCreationDraftInvoiceComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        title: 'Create the draft invoice ?',
        message: message,
        confirmText: 'Create the invoice',
        cancelText: 'Cancel',
        confirmColor: 'primary' as 'primary' | 'accent' | 'warn',
        cancelColor: 'warn' as 'primary' | 'accent' | 'warn',
        invoiceDetails: {
          corporate: corporate,
          address: address,
          city: city,
          country: country,
          sdi: sdi || ''
        },
        feeVatSummary: feeVatSummary || [],
        pennylaneCountryLabel: this.formatPennylaneCountryLabel(pennylaneCountry)
      } as ConfirmationCreationDraftInvoiceData,
      enterAnimationDuration: '400ms',
      exitAnimationDuration: '300ms',
      disableClose: false
    });

    return dialogRef.afterClosed();
  }

  confirmInvoiceCreationForTimeManagement(
    company_name: string,
    deal_name: string,
    address: string,
    city: string,
    country: string,
    vatRate: number | string = 0
  ): Observable<InvoiceCreationResult> {
    const message = `You are about to pass all the time entries to billing for the company "${company_name}" and the deal "${deal_name}".`;

    return this.configApiService.getAllConfigApis().pipe(
      map((response: any) => {
        const rows = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
        const options = rows
          .filter((api: any) => {
            const type = (api?.type || '').toString().toLowerCase();
            const status = (api?.status || '').toString().toLowerCase();
            return type === 'pennylane' && (status === 'active' || status === '1');
          })
          .map((api: any) => ({
            name: String(api?.name || 'Pennylane'),
            country: this.resolveApiCountry(api)
          }))
          .filter((option: any) => !!option.country)
          .sort((a: any, b: any) => a.name.localeCompare(b.name));

        return options;
      }),
      catchError(() => of([])),
      switchMap((pennylaneOptions: Array<{ name: string; country: string | null }>) => {
        const dialogRef = this.dialog.open(ConfirmationCreationDraftInvoiceComponent, {
          width: '500px',
          maxWidth: '90vw',
          data: {
            title: `Pass all the time entries to billing ?`,
            message: message,
            confirmText: 'Pass all the time entries to billing',
            cancelText: 'Cancel',
            confirmColor: 'primary' as 'primary' | 'accent' | 'warn',
            cancelColor: 'warn' as 'primary' | 'accent' | 'warn',
            invoiceDetails: {
              corporate: company_name,
              address: address,
              city: city,
              country: country,
              vatRate: vatRate
            },
            showGlobalVatInput: true,
            pennylaneOptions,
            selectedPennylaneCountry: null,
            pennylaneCountryLabel: this.formatPennylaneCountryLabel(null)
          } as ConfirmationCreationDraftInvoiceData,
          enterAnimationDuration: '400ms',
          exitAnimationDuration: '300ms',
          disableClose: false
        });

        return dialogRef.afterClosed();
      })
    );
  }

  private resolveApiCountry(api: any): string | null {
    const rawCountry = api?.country ? String(api.country).trim().toLowerCase() : '';
    const isValidSlug = /^[a-z][a-z0-9_-]*$/.test(rawCountry);
    const blockedValues = new Set(['1', '0', 'true', 'false', 'null', 'undefined']);

    if (rawCountry && isValidSlug && !blockedValues.has(rawCountry)) {
      return rawCountry;
    }

    const name = (api?.name || '').toString().trim().toLowerCase();
    if (name === 'pennylane') {
      return 'france';
    }
    if (name.startsWith('pennylane ')) {
      return name.replace('pennylane ', '').trim() || null;
    }

    return null;
  }

  /**
   * Confirmation before finalizing an invoice, with an invoice date picker.
   * @param title dialog title
   * @param message HTML message
   * @param invoiceDate invoice date
   * @param minDate earliest selectable date
   * @param maxDate latest selectable date
   * @returns Observable<InvoiceFinalizationResult>
   */
  confirmInvoiceFinalization(
    title: string,
    message: string,
    invoiceDate?: Date,
    minDate?: Date,
    maxDate?: Date,
    pennylaneCountry?: string | null
  ): Observable<InvoiceFinalizationResult> {
    const dialogRef = this.dialog.open(ConfirmationFinalizingInvoiceComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        title: title,
        message: message,
        confirmText: 'Update and Finalize',
        cancelText: 'Cancel',
        confirmColor: 'primary',
        cancelColor: 'warn',
        invoiceDate: invoiceDate,
        minDate: minDate,
        maxDate: maxDate,
        showDatePicker: true,
        pennylaneCountryLabel: this.formatPennylaneCountryLabel(pennylaneCountry)
      } as ConfirmationFinalizingInvoiceData,
      enterAnimationDuration: '400ms',
      exitAnimationDuration: '300ms',
      disableClose: true
    });

    return dialogRef.afterClosed();
  }

  private formatPennylaneCountryLabel(country: string | null | undefined): string {
    const normalized = (country || '').toString().trim();
    if (!normalized) {
      return 'Current workspace';
    }
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }


  /**
   * Choice between two actions.
   * @param data choice dialog content
   * @param enterAnimationDuration enter animation duration (optional)
   * @param exitAnimationDuration exit animation duration (optional)
   * @returns Observable<ChooseDialogResult> - 'option1', 'option2' or null
   */
  chooseAction(
    data: ChooseDialogData,
    enterAnimationDuration: string = '300ms',
    exitAnimationDuration: string = '200ms'
  ): Observable<ChooseDialogResult> {
    const dialogRef = this.dialog.open(ChooseDialogComponent, {
      width: '550px',
      maxWidth: '95vw',
      data: data,
      enterAnimationDuration,
      exitAnimationDuration,
      disableClose: data.disableClose === true
    });

    return dialogRef.afterClosed();
  }

  /**
   * Choice between three actions.
   * @param data choice dialog content
   * @param enterAnimationDuration enter animation duration (optional)
   * @param exitAnimationDuration exit animation duration (optional)
   * @returns Observable<ChooseThreeDialogResult> - 'option1', 'option2', 'option3' or null
   */
  chooseThreeActions(
    data: ChooseThreeDialogData,
    enterAnimationDuration: string = '300ms',
    exitAnimationDuration: string = '200ms'
  ): Observable<ChooseThreeDialogResult> {
    const dialogRef = this.dialog.open(ChooseThreeDialogComponent, {
      width: '850px',
      maxWidth: '95vw',
      data: data,
      enterAnimationDuration,
      exitAnimationDuration,
      disableClose: false
    });

    return dialogRef.afterClosed();
  }

  /**
   * File upload with drag and drop.
   * @param data dialog content
   * @param enterAnimationDuration enter animation duration (optional)
   * @param exitAnimationDuration exit animation duration (optional)
   * @returns Observable<DropFileDialogResult> - the selected file
   */
  dropFile(
    data: DropFileDialogData,
    enterAnimationDuration: string = '300ms',
    exitAnimationDuration: string = '200ms'
  ): Observable<DropFileDialogResult> {
    const dialogRef = this.dialog.open(DropFileDialogComponent, {
      width: '550px',
      maxWidth: '90vw',
      data: data,
      enterAnimationDuration,
      exitAnimationDuration,
      disableClose: false
    });

    return dialogRef.afterClosed();
  }

  /**
   * Text input.
   * @param data dialog content
   * @param enterAnimationDuration enter animation duration (optional)
   * @param exitAnimationDuration exit animation duration (optional)
   * @returns Observable<InputTextDialogResult> - the typed value
   */
  inputText(
    data: InputTextDialogData,
    enterAnimationDuration: string = '300ms',
    exitAnimationDuration: string = '200ms'
  ): Observable<InputTextDialogResult> {
    // Keep the current scroll position
    const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    
    const widePennylanePicker = !!(data.pennylaneOptions && data.pennylaneOptions.length > 0);
    const dialogRef = this.dialog.open(InputTextDialogComponent, {
      width: widePennylanePicker ? '560px' : '450px',
      maxWidth: widePennylanePicker ? '96vw' : '90vw',
      data: data,
      enterAnimationDuration,
      exitAnimationDuration,
      disableClose: false,
      autoFocus: false,
      panelClass: widePennylanePicker ? 'input-text-dialog-pennylane-pane' : undefined
    });

    // Restore the scroll position once the dialog is open
    setTimeout(() => {
      if (window.scrollY !== scrollY) {
        window.scrollTo(0, scrollY);
      }
    }, 0);

    return dialogRef.afterClosed().pipe(
      tap(() => {
        // Restore the scroll position once the dialog is closed
        setTimeout(() => {
          if (window.scrollY !== scrollY) {
            window.scrollTo(0, scrollY);
          }
        }, 0);
      })
    );
  }

  promptCompanySdiInput(
    corporateName: string,
    currentSdi: string = '',
    required: boolean = false
  ): Observable<InputTextDialogResult> {
    return this.inputText({
      title: 'Company SDI number',
      message: `SDI number for company <strong>${corporateName || '-'}</strong>:`,
      inputLabel: 'SDI number',
      inputPlaceholder: 'Enter 7 characters',
      inputValue: currentSdi || '',
      confirmText: 'Save SDI',
      cancelText: required ? 'Cancel' : 'Continue without SDI',
      required,
      minLength: required ? 7 : undefined,
      maxLength: 7,
      pattern: '^.{7}$',
      patternErrorMessage: 'The SDI number must contain exactly 7 characters.'
    });
  }

  /**
   * Timesheet validation, showing the company and the deal.
   * @param data dialog content
   * @param enterAnimationDuration enter animation duration (optional)
   * @param exitAnimationDuration exit animation duration (optional)
   * @returns Observable<ValidateTimesheetDialogResult> - the validation result
   */
  validateTimesheet(
    data: ValidateTimesheetDialogData,
    enterAnimationDuration: string = '400ms',
    exitAnimationDuration: string = '300ms'
  ): Observable<ValidateTimesheetDialogResult> {
    const dialogRef = this.dialog.open(ValidateTimesheetDialogComponent, {
      width: '580px',
      maxWidth: '95vw',
      data: data,
      enterAnimationDuration,
      exitAnimationDuration,
      disableClose: false
    });

    return dialogRef.afterClosed();
  }




  


  // Notifications: success / warning / info stay toasts; an error always opens a dialog.

  showNotification(message: string, title: string = '', type: string = 'success') {
    if (type == 'success') {
      this.toast.success(message, title);
    } else if (type == 'error') {
      this.showErrorDialog(message, title);
    } else if (type == 'warning') {
      this.toast.warning(message, title);
    } else if (type == 'info') {
      this.toast.info(message, title);
    }
  }

  /**
   * Error notification: a dialog closed with OK, never a toast (project rule — an error that
   * disappears on its own is not read). Opened on top of any open dialog rather than replacing it,
   * so an error raised from inside a form does not throw the form away. The same error shown twice
   * in a row (a loop, a retried request) opens a single dialog.
   */
  private openErrorKeys = new Set<string>();

  /**
   * @param links optional buttons that take the user where the action is done; the promise
   *   resolves with the clicked link id, or null on OK (and immediately when the same error is
   *   already open).
   */
  showErrorDialog(message: any, title: any = '', links: ErrorNotificationLink[] = []): Promise<string | null> {
    const content: ErrorNotificationContent = { ...resolveErrorNotificationContent(message, title), links };
    const key = `${content.title}\n${content.message}`;
    if (this.openErrorKeys.has(key)) {
      return Promise.resolve(null);
    }
    this.openErrorKeys.add(key);

    const dialogRef = this.dialog.open<ErrorNotificationDialogComponent, ErrorNotificationContent, string | boolean>(ErrorNotificationDialogComponent, {
      data: content,
      // Wide when the message lists actions (one bullet line each) or is long.
      width: errorNotificationDialogWidth(content.message),
      maxWidth: '92vw',
      disableClose: true,
      panelClass: 'error-notification-dialog-panel',
    });
    return new Promise((resolve) => {
      dialogRef.afterClosed().subscribe((result) => {
        this.openErrorKeys.delete(key);
        resolve(typeof result === 'string' ? result : null);
      });
    });
  }

  /**
   * "Missing information" alert (e.g. deal execution): orange ngx-toastr background with a
   * triangle icon, not the error style.
   */
  showMissingInformationAlert(message: string, title: string = 'Missing information'): void {
    this.toast.warning(message, title, {
      toastClass: 'ngx-toastr toast-alert-orange',
      timeOut: 10000,
    });
  }

    // ========== SPINNER ==========

  /**
   * Shows the global spinner with an optional message.
   * @param message optional message shown under the spinner
   */
  showSpinner(message?: string): void {
    if (message) {
      this.spinnerMessageSubject.next(message);
    }
    this.spinnerLoadingSubject.next(true);
  }

  /**
   * Hides the spinner and clears its message.
   */
  hideSpinner(): void {
    this.spinnerLoadingSubject.next(false);
    this.spinnerMessageSubject.next('');
  }




  /**
   * Updates the spinner message without hiding it.
   * @param message new message
   */
  updateSpinnerMessage(message: string): void {
    this.spinnerMessageSubject.next(message);
  }

  /**
   * Whether the spinner is currently shown.
   * @returns true when the spinner is visible
   */
  isSpinnerLoading(): boolean {
    return this.spinnerLoadingSubject.value;
  }

}
